<template>
    <section v-if="visible && (loading || error || orders.length)" class="batch-notice" aria-live="polite">
        <div class="batch-notice-header">
            <strong v-if="!loading && !error">{{ summary }}</strong>
            <span v-else>{{ loading ? '正在核对同批处方' : '同批处方状态待核实' }}</span>
            <el-button link type="primary" :loading="loading" :disabled="disabled" @click="loadOrders">刷新状态</el-button>
        </div>
        <div v-if="loading">正在查询同批其他处方，请稍候…</div>
        <div v-else-if="error" class="batch-notice-error">{{ error }} 请点击“刷新状态”重试，勿据此判断全部已支付。</div>
        <div v-else class="batch-notice-orders">
            <div v-for="order in orders" :key="order.orderId" class="batch-notice-order">
                <el-tag :type="statusInfo(order).type" size="small" effect="light">{{ statusInfo(order).label }}</el-tag>
                <span>{{ order.serialNo || '单号暂不可用' }}</span>
            </div>
        </div>
    </section>
</template>

<script>
import {getDoctorPrescriptionGroupOrders} from '@/api';

export default {
    name: 'PrescriptionGroupNotice',
    props: {
        orderId: {type: [Number, String], default: null},
        visible: {type: Boolean, default: false},
        disabled: {type: Boolean, default: false},
        refreshKey: {type: String, default: ''}
    },
    data() {
        return {orders: [], loading: false, error: '', requestId: 0};
    },
    computed: {
        summary() {
            const pendingCount = this.orders.filter(order => String(order.status) === '3' && !order.payTime).length;
            if (pendingCount) return `当前患者本次开方还有 ${pendingCount} 张其他处方待支付，请核对后逐张办理，避免漏收。`;
            if (this.orders.every(order => this.statusInfo(order).label === '已支付')) {
                return '同批其他处方均已支付，本次只需办理当前订单。';
            }
            if (this.orders.some(order => ['支付待核实', '状态待核实'].includes(this.statusInfo(order).label))) {
                return '部分同批处方支付状态尚待核实，请刷新并核对收款记录，不要据此判断全部已支付。';
            }
            return '同批其他处方暂无待支付订单，请核对各单实际状态；取消、退款或未审核完成不代表已支付。';
        }
    },
    watch: {
        visible: {immediate: true, handler() { this.reloadContext(); }},
        orderId() { this.reloadContext(); },
        // 当前单支付状态变化后，只读刷新同批订单，不推断其他订单已支付。
        refreshKey() { if (this.visible) this.loadOrders(); }
    },
    beforeUnmount() {
        this.requestId++;
    },
    methods: {
        reloadContext() {
            this.requestId++;
            this.orders = [];
            this.error = '';
            this.loading = false;
            if (this.visible && this.orderId) this.loadOrders();
        },
        statusInfo(order) {
            const status = String(order.status);
            if (status === '3') return order.payTime
                ? {label: '支付待核实', type: 'warning'} : {label: '待支付', type: 'warning'};
            if (status === '8') return {label: '已取消', type: 'info'};
            if (status === '13') return {label: '已退款', type: 'danger'};
            // 人工收款成功后订单进入待接单（1）；药房物流与门诊待取药也可已支付。
            // 必须同时具备支付时间，不能单凭状态推断真实到账。
            if (['1', '5', '6', '7', '9', '11', '12'].includes(status)) {
                return order.payTime ? {label: '已支付', type: 'success'} : {label: '支付待核实', type: 'info'};
            }
            const labels = {'2': '待转方', '4': '未通过审核', '10': '已超时'};
            return {label: labels[status] || '状态待核实', type: 'info'};
        },
        async loadOrders() {
            if (!this.visible || !this.orderId) return;
            const requestId = ++this.requestId;
            const orderId = this.orderId;
            this.loading = true;
            this.error = '';
            this.orders = [];
            const isCurrent = () => requestId === this.requestId && this.visible && String(orderId) === String(this.orderId);
            try {
                // 开方页面使用医生范围查询，与 clinicFee 收费端接口保持分离。
                const response = await getDoctorPrescriptionGroupOrders(orderId);
                if (!isCurrent()) return;
                const payload = response && response.data;
                if (!payload || payload.code !== 200 || !Array.isArray(payload.data)
                    || payload.data.some(order => !order || order.orderId === null || order.orderId === undefined)) {
                    throw new Error('查询失败');
                }
                this.orders = payload.data.filter(order => String(order.orderId) !== String(orderId));
            } catch (error) {
                if (isCurrent()) this.error = '暂无法确认同批其他处方的支付状态。';
            } finally {
                if (isCurrent()) this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.batch-notice { width: 100%; box-sizing: border-box; padding: 8px 10px; margin-bottom: 8px; border: 1px solid var(--el-color-primary-light-7); border-radius: 6px; background: var(--el-color-primary-light-9); color: var(--el-text-color-regular); font-size: 12px; line-height: 1.6; }
.batch-notice-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.batch-notice-header :deep(.el-button) { flex-shrink: 0; }
.batch-notice-orders { display: flex; flex-wrap: wrap; gap: 4px 8px; max-height: 96px; overflow-y: auto; }
.batch-notice-order { display: flex; align-items: center; gap: 5px; min-width: 0; max-width: 100%; padding: 3px 6px; border: 1px solid var(--el-color-primary-light-7); border-radius: 5px; background: var(--el-fill-color-blank); overflow-wrap: anywhere; }
.batch-notice-order :deep(.el-tag) { flex-shrink: 0; }
.batch-notice-error { color: var(--el-color-warning-dark-2); }
</style>
